import { createClient } from '@/lib/supabase/client';

export interface TeamMember {
  id: string;
  fullName: string;
  jobTitle: string;
  bio: string;
  avatarUrl?: string;
  linkedinUrl?: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TeamMemberInput {
  fullName: string;
  jobTitle: string;
  bio: string;
  avatarUrl?: string;
  linkedinUrl?: string;
  displayOrder: number;
  isActive?: boolean;
}

class TeamService {
  async getAll(): Promise<TeamMember[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('team_members')
      .select('*')
      .order('display_order', { ascending: true });

    if (error) throw new Error(error.message);
    
    return (data || []).map(this.mapFromDb);
  }

  async getActive(): Promise<TeamMember[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('team_members')
      .select('*')
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (error) throw new Error(error.message);
    
    return (data || []).map(this.mapFromDb);
  }

  async getById(id: string): Promise<TeamMember | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('team_members')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw new Error(error.message);
    
    return data ? this.mapFromDb(data) : null;
  }

  async create(input: TeamMemberInput): Promise<TeamMember | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('team_members')
      .insert([{
        full_name: input.fullName,
        job_title: input.jobTitle,
        bio: input.bio,
        avatar_url: input.avatarUrl,
        linkedin_url: input.linkedinUrl,
        display_order: input.displayOrder,
        is_active: input.isActive ?? true,
        created_by: user.id,
      }])
      .select()
      .single();

    if (error) throw new Error(error.message);
    
    return data ? this.mapFromDb(data) : null;
  }

  async update(id: string, input: Partial<TeamMemberInput>): Promise<TeamMember | null> {
    const supabase = createClient();
    const updateData: any = {};
    
    if (input.fullName !== undefined) updateData.full_name = input.fullName;
    if (input.jobTitle !== undefined) updateData.job_title = input.jobTitle;
    if (input.bio !== undefined) updateData.bio = input.bio;
    if (input.avatarUrl !== undefined) updateData.avatar_url = input.avatarUrl;
    if (input.linkedinUrl !== undefined) updateData.linkedin_url = input.linkedinUrl;
    if (input.displayOrder !== undefined) updateData.display_order = input.displayOrder;
    if (input.isActive !== undefined) updateData.is_active = input.isActive;

    const { data, error } = await supabase
      .from('team_members')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw new Error(error.message);
    
    return data ? this.mapFromDb(data) : null;
  }

  async delete(id: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('team_members')
      .delete()
      .eq('id', id);

    if (error) throw new Error(error.message);
  }

  private mapFromDb(data: any): TeamMember {
    return {
      id: data.id,
      fullName: data.full_name,
      jobTitle: data.job_title,
      bio: data.bio,
      avatarUrl: data.avatar_url,
      linkedinUrl: data.linkedin_url,
      displayOrder: data.display_order,
      isActive: data.is_active,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  }
}

export const teamService = new TeamService();